const RATING_KEYS = [
  "starRating",
  "star_rating",
  "rating",
  "averageRating",
  "average_rating",
  "sourceRating",
  "source_rating",
  "googleRating",
  "google_rating",
  "overallRating",
  "overall_rating",
];

const REVIEW_COUNT_KEYS = [
  "reviewCount",
  "review_count",
  "reviewsCount",
  "reviews_count",
  "ratingsCount",
  "ratings_count",
  "ratingCount",
  "rating_count",
  "userRatingsTotal",
  "user_ratings_total",
  "totalReviews",
  "total_reviews",
  "sourceReviewCount",
  "source_review_count",
];

const COMMENT_COUNT_KEYS = [
  "commentCount",
  "comment_count",
  "commentsCount",
  "comments_count",
  "writtenReviewCount",
  "written_review_count",
  "sourceCommentCount",
  "source_comment_count",
];

const YEAR_KEYS = ["yearEstablished", "year_established", "yearsInBusiness", "years_in_business"];

const PRICE_LEVEL_KEYS = ["priceLevel", "price_level"];

const VERIFIED_KEYS = ["sourceVerified", "source_verified", "isVerified", "is_verified", "claimed"];

const RATING_SOURCE_KEYS = ["ratingSource", "rating_source", "reputationSource", "reputation_source"];

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function collectObjects(candidate = {}) {
  const payload = candidate.rawPayload || {};
  const mapped = payload.mapped || {};
  const rawMetadata = payload.rawMetadata || {};
  const reputation = payload.reputation || rawMetadata.reputation || mapped.reputation || {};
  const socialProof = payload.socialProof || rawMetadata.socialProof || mapped.socialProof || {};
  const normalization = candidate.normalizationData?.fields || {};

  return [reputation, socialProof, mapped, rawMetadata, payload, normalization, candidate];
}

function pickFirstValue(objects, keys) {
  for (const source of objects) {
    if (!isPlainObject(source)) continue;
    for (const key of keys) {
      const value = source[key];
      if (value != null && value !== "") return value;
    }
  }
  return null;
}

function toNumber(value) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toCount(value) {
  const parsed = toNumber(value);
  if (parsed == null) return null;
  return Math.max(0, Math.round(parsed));
}

function toBoolean(value) {
  if (value === true || value === "true" || value === 1 || value === "1") return true;
  if (value === false || value === "false" || value === 0 || value === "0") return false;
  return null;
}

function clampRating(value) {
  const rating = toNumber(value);
  if (rating == null) return null;
  return Math.min(5, Math.max(0, Math.round(rating * 10) / 10));
}

function inferRatingSource(candidate = {}, explicitSource = null) {
  if (explicitSource) return String(explicitSource);
  const payload = candidate.rawPayload || {};
  if (payload.sourceUrl?.includes("google")) return "google";
  if (candidate.sourceName) return candidate.sourceName;
  if (candidate.sourceId) return candidate.sourceId;
  return null;
}

function deriveTrustBand({ rating, reviewCount }) {
  if (rating == null && reviewCount == null) return "none";
  if (rating != null && rating >= 4.5 && reviewCount != null && reviewCount >= 25) return "strong";
  if (rating != null && rating >= 4.0 && reviewCount != null && reviewCount >= 10) return "good";
  if (rating != null && reviewCount != null && reviewCount >= 5) return "moderate";
  if (rating != null || reviewCount != null) return "limited";
  return "none";
}

export function getCandidateReputationSignals(candidate = {}) {
  const objects = collectObjects(candidate);
  const rating = clampRating(pickFirstValue(objects, RATING_KEYS));
  const reviewCount = toCount(pickFirstValue(objects, REVIEW_COUNT_KEYS));
  const ratingsCount = toCount(
    pickFirstValue(objects, ["ratingsCount", "ratings_count", "ratingCount", "rating_count", "userRatingsTotal", "user_ratings_total"])
  );
  const commentCount = toCount(pickFirstValue(objects, COMMENT_COUNT_KEYS));
  const yearEstablished = toCount(pickFirstValue(objects, YEAR_KEYS));
  const priceLevel = toCount(pickFirstValue(objects, PRICE_LEVEL_KEYS));
  const sourceVerified = toBoolean(pickFirstValue(objects, VERIFIED_KEYS));
  const ratingSource = inferRatingSource(
    candidate,
    pickFirstValue(objects, RATING_SOURCE_KEYS)
  );

  const yearsInBusiness =
    yearEstablished != null && yearEstablished > 1900 && yearEstablished <= new Date().getFullYear()
      ? new Date().getFullYear() - yearEstablished
      : toCount(pickFirstValue(objects, ["yearsInBusiness", "years_in_business"]));

  const trustBand = deriveTrustBand({ rating, reviewCount });
  const hasData =
    rating != null ||
    reviewCount != null ||
    commentCount != null ||
    yearsInBusiness != null ||
    priceLevel != null ||
    sourceVerified != null;

  return {
    rating,
    reviewCount,
    ratingsCount: ratingsCount ?? reviewCount,
    commentCount,
    yearEstablished,
    yearsInBusiness,
    priceLevel,
    sourceVerified,
    ratingSource,
    trustBand,
    hasData,
  };
}

export function formatReputationRating(value, locale = "en-US") {
  const rating = clampRating(value);
  if (rating == null) return "—";
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(rating);
}
